/**
 * 일반결제 > 타입
 */
export const GeneralSkip = {
    VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
} as const

export type GeneralSkipType = Constant<typeof GeneralSkip>

/**
 * 빌링 카드 코드 (PaymentsCardCode) 로 커버되지 않은 신규 발급사 코드를 임의로 빌링 카드 코드 형식으로 정의
 * npayBiz 과제부터 신규로 발급되는 카드코드에 대응하기 위한 값으로 order-web 내부에만 존재
 */
export const ExtraCardIssuerCode = {
    /** IBK기업은행 */
    IBK: 'BC_IBK',
    /** BC우리카드 */
    BC_WOORI: 'BC_WOORI',
    /** 우체국예금보험 */
    POST_INSURANCE: 'POST',
    /** 저축은행중앙회 */
    SAVING_BANK: 'SAVING_BANK',
    /** 새마을금고 */
    SAMAEUL: 'SAMAEUL',
    /** 토스뱅크 */
    TOSS_BANK: 'TOSS_BANK',
    /** 페이코 */
    PAYCO: 'PAYCO',
} as const

export const PaymentsCardCode = {
    /** 국민 */
    KB: 'C3',
    /** 비씨 */
    BC: 'C1',
    /** 신한 */
    SHINHAN: 'C0',
    /** 현대 */
    HYUNDAI: 'CH',
    /** 삼성 */
    SAMSUNG: 'C7',
    /** 롯데 */
    LOTTE: 'C5',
    /** 외환 */
    KEB: 'CA',
    /** NH */
    NH: 'C4',
    /** 하나 */
    HANA: 'CF',
    /** 씨티 */
    CITI: 'C9',
    /** UnionPay(銀聯)/해외발급 */
    UNIONPAY: 'CR',
    /** 카카오뱅크 */
    KAKAO: 'CP',
    /** 우리 */
    WOORI: 'CB',
    /** 광주 */
    GWANGJU: 'C2',
    /** 씨티 */
    CITY: 'C9',
    /** 전북 */
    JB: 'CC',
    /** 케이뱅크 */
    KBANK: 'CQ',
    /** 수협 */
    SUHYUP: 'C8',
    /** 산은 */
    KDB: 'C6',
    /** 제주 */
    JEJU: 'CD',
    /** 해외비자 */
    VISA: 'CJ',
    /** 해외마스터 */
    MASTER: 'CK',
    /** 해외JCB */
    JCB: 'CI',
    /** 해외아멕스 */
    AMEX: 'CN',
    /** 해외다이너스 */
    DINERS: 'CL',
    /** 해외디스커버 */
    DISCOVER: 'CM',
    /** 유니온페이(银联) */
    UNION: 'CR',
    /** 기타 */
    ETC: 'NC',
    ...ExtraCardIssuerCode,
} as const

export type PaymentsCardCodeType = Constant<typeof PaymentsCardCode>

// CardCode => myDataOrgCode
export const myDataOrgCardCodeMapper: Partial<Record<PaymentsCardCodeType, string>> = {
    [PaymentsCardCode.SHINHAN]: 'D1AAEX0000', // 신한카드
    [PaymentsCardCode.SAMSUNG]: 'D1AAEV0000', // 삼성카드
    [PaymentsCardCode.KB]: 'D1AAAF0000', // KB국민카드
    [PaymentsCardCode.HYUNDAI]: 'D1AAFO0000', // 현대카드
    [PaymentsCardCode.BC]: 'D1AAFQ0000', // 비씨카드
    [PaymentsCardCode.HANA]: 'D1AABJ0000', // 하나카드
    [PaymentsCardCode.WOORI]: 'D1AAAH0000', // 우리카드
    [PaymentsCardCode.LOTTE]: 'D1AADO0000', // 롯데카드
    [PaymentsCardCode.NH]: 'D1AABG0000', // NH농협카드
    [PaymentsCardCode.KAKAO]: 'D1AAHJ0000', // 카카오뱅크카드
    [PaymentsCardCode.CITI]: 'D1AAQE0000', // 씨티카드
}

/** 나중에 무통장 입금 은행코드 */
export const PaymentsVirtualAccountCode = {
    NONG_HYUB: '011',
    KB_STAR: '004',
    SHINHAN: '088',
    WOORI_BANK: '020',
    IBK: '003',
    HANA_BANK: '081',
    DGB: '031',
    BUSAN_BANK: '032',
    EPOST_BANK: '071',
    STANDARD_CHATERED: '023',
    KJ_BANK: '034',
    KB_BANK: '039',
    SUHYUP_BANK: '007',
    K_BANK: '089',
} as const

/** 환불 구분 */
export const RefundCode = {
    /** 본인계좌환불 */
    BANK_ACCOUNT: 'BANK_ACCOUNT',
    /** 환불정산액 적립 */
    CHARGE_AMOUNT: 'CHARGE_AMOUNT',
} as const

export type RefundCodeType = Constant<typeof RefundCode>

export const CardType = {
    CREDIT: 'CREDIT',
    CHECK: 'CHECK',
    PREPAID: 'PREPAID',
    NFC_PREPAID: 'NFC_PREPAID',
} as const

export type CardTypeType = Constant<typeof CardType>

export const CardOwner = {
    /** 개인 */
    PERSONAL: 'personal',
    /** 가족 */
    FAMILY: 'family',
    /** 법인_지정 */
    CORP: 'corporation',
    /** 법인_공용 */
    CORP_PUBLIC: 'corporation_public',
    /** 선불 */
    PREPAID: 'prepaid',
} as const

export type CardOwnerType = Constant<typeof CardOwner>

/**
 * 카드 구분
 * @see https://oss.fin.navercorp.com/CardPayment/wiki/wiki/%EC%B9%B4%EB%93%9C%EC%82%AC%EC%9A%A9%EC%9E%90%EA%B5%AC%EB%B6%84
 * */
export const CardOwnerGroup = {
    PRIVATE: 'PRIVATE',
    CORPORATION: 'CORPORATION',
} as const

export type CardCodeType = Constant<typeof CardOwnerGroup>

export type CardCompanyCodeType = string

/**
 * 고정값으로 사용가능한 충전결제시 적립비율 케이스
 */ export const ChargeBenefitPointRate = {
    /** 최대 3% 적립 */
    MAX: 'MAX',
    /** 3% 적립 */
    THREE: 'THREE',
    /** 2.5% 적립 */
    TWO_HALF: 'TWO_HALF',
    /** 1% 적립 */
    ONE: 'ONE',
    /** 최대 2.5% 적립 */
    TWO_HALF_MAX: 'TWO_HALF_MAX',
} as const

export type ChargeBenefitPointRateType = Constant<typeof ChargeBenefitPointRate>

export const ChargeBenefitPointRateNumber = {
    [ChargeBenefitPointRate.MAX]: 3,
    [ChargeBenefitPointRate.THREE]: 3,
    [ChargeBenefitPointRate.TWO_HALF]: 2.5,
    [ChargeBenefitPointRate.TWO_HALF_MAX]: 2.5,
    [ChargeBenefitPointRate.ONE]: 1,
} as const

/** 휴대폰 결제 */
export const GeneralMobileCode = {
    /** 간편결제 */
    MOBILE_PIN: 'MOBILEPIN',
    /** 일반결제 */
    MOBILE: 'MOBILE',
} as const

export type GeneralMobileCodeType = Constant<typeof GeneralMobileCode>

/** 현금 영수증 발행 유형 */
export const CashReceiptIssueType = {
    PR_CELL_PHONE_NUMBER: 'PR_CELL_PHONE_NUMBER',
    PR_SOCIAL_NUMBER: 'PR_SOCIAL_NUMBER',
    PR_CASH_RECEIPT_CARD_NUMBER: 'PR_CASH_RECEIPT_CARD_NUMBER',
    BUSINESS_REGISTRATION_NUMBER: 'BUSINESS_REGISTRATION_NUMBER',
} as const

export type CashReceiptIssueTypeType = Constant<typeof CashReceiptIssueType>

/** 현금영수증 발급 용도 */
export const CashReceiptIssueUsage = {
    /** 개인소득공제 */
    PERSONAL_INCOME_TAX: 'PERSONAL_INCOME_TAX',
    /** 사업자증빙용・세금계산서 */
    BIZ_EXPENSE_PROOF: 'BIZ_EXPENSE_PROOF',
} as const

export type CashReceiptIssueUsageType = Constant<typeof CashReceiptIssueUsage>

/** 현금영수증 발행 추가정책 */
export const ReceiptAdditionalPolicy = {
    NFC_PREPAID: 'NFC_PREPAID',
} as const

export type ReceiptAdditionalPolicyType = Constant<typeof ReceiptAdditionalPolicy>

// 카드결제 할부기간 일시불
export const NonInstallment = '1'

// 일시불일 경우 할부 정보
export const NonInstallmentInfo = {month: 1, minAmount: 0}

// 미신원 유저의 포인트 사용 최대 보유/사용 포인트
export const NaverCashUsageAmountLimit = 500000

export const InterestFreeInstallmentType = {
    /** (FE 전용) 유이자 */
    NONE: 'NONE',
    /** 전체 */
    ALL: 'ALL',
    /** 부분 */
    PART: 'PART',
    /** @deprecated */
    PRODUCT: 'PRODUCT',
} as const

type DeriveType<T> = T[keyof T]

export type InterestFreeInstallmentTypeType = DeriveType<typeof InterestFreeInstallmentType>

/** 무이자 할부 목록 */
export const FreeInstallmentTypes: InterestFreeInstallmentTypeType[] = [
    InterestFreeInstallmentType.ALL,
    InterestFreeInstallmentType.PART,
]

export const InterestBurdenType = {
    /** PG */
    PG: 'PG',
    /** MP (할부 부담 주체가 판매자 or 네이버페이) */
    MP: 'MP',
    /** MP+seller */
    MP_SELLER: 'MP_SELLER',
    CUSTOMER: 'CUSTOMER',
    /** (FE 전용) */
    NONE: 'NONE',
} as const

export type InterestBurdenTypeType = DeriveType<typeof InterestBurdenType> | ''

/** 할부 상태 */
export const InterestFreeStatus = {
    /** 일시불 만 가능 */
    ONLY_FULL: 'ONLY_FULL',
    /** 기본 12개월 할부 가능 */
    INSTALLMENT: 'INSTALLMENT',
} as const

export type InterestFreeStatusType = Constant<typeof InterestFreeStatus>

export const LongFreeInstallmentStatus = {
    /** 장기무이자 X */
    NONE: 'NONE',
    /** 장기무이자 O */
    LONG_FREE: 'LONG_FREE',
} as const

export type LongFreeInstallmentStatusType = DeriveType<typeof LongFreeInstallmentStatus>

export const CafeAppUnavailableCardCodes = (() => {
    // CB 우리카드도 포함되어야하는데 선택이 불가하다
    const {BC, KB, HYUNDAI, NH, HANA, KEB} = PaymentsCardCode
    return [BC, KB, HYUNDAI, NH, HANA, KEB]
})()

/**
 * 페이머니 가입자가 사용할 수 있는 최대 페이머니 포인트
 * - 페이머니 + 충전후 사용 페이머니
 **/
export const MaximumUsingChargePointAmount = 2000000

/**
 * 충전 최소 금액
 */
export const MinimumChargePointAmount = 10000

/** 충전포인트 충전 제한 요인 */
export const RechargePointLimitType = {
    // 보유 잔액 한도
    BALANCE: 'BALANCE',
    // 계좌간편결제 월 충전 한도
    MONTH: 'MONTH',
    // 1회 최대 결제 가능 금액 제한
    ONCE: 'ONCE',
    // 미성년자 한도
    MINOR: 'MINOR',
} as const

export type RechargePointLimitTypeType = Constant<typeof RechargePointLimitType>

export type PayMoneyUserStatusValueType = '1.0' | '2.0' | '3.0' | '3.1' | '3.2' | '4.0' | '4.1' | '4.2'

/** 페이머니 가입자 상태 */
export const PayMoneyUserValueStatus: Record<PayMoneyUserStatusType, PayMoneyUserStatusValueType> = {
    // 1.0 : 페이머니, 하나통장 미개설
    NOT_OPEN: '1.0',
    // 2.0 : 페이머니 > 페이머니 통장 잔액 이체 실패
    TRANSFER_FAIL: '2.0',
    // 3.0 : 페이머니 하나통장 연결 해지
    DISCONNECTED: '3.0',
    // 3.1: 미래에셋 페이머니 신설 이후 네통 개설 고객 (정상 결제 가능)
    READY_OPEN: '3.1',
    // 3.2: 미래에셋 페이머니 신설 이전 네통 개설 고객 (추후 삭제될 버전 정보) (정상 결제 가능)
    NOT_READY_OPEN: '3.2',
    // 4.0: 페이머니 - 하나통장 개설 (정상)
    OPEN: '4.0',
    // 4.1: 거래중지
    STOP_TRADING: '4.1',
    // 4.2: 하나통장 간편결제 계좌 삭제
    EASY_BANK_DELETED: '4.2',
} as const

/** 페이머니 가입자 상태 */
export const PayMoneyUserStatus: Record<PayMoneyUserStatusType, PayMoneyUserStatusType> = {
    // 1.0 : 페이머니, 하나통장 미개설
    NOT_OPEN: 'NOT_OPEN',
    // 2.0 : 페이머니 > 페이머니 통장 잔액 이체 실패
    TRANSFER_FAIL: 'TRANSFER_FAIL',
    // 3.0 : 페이머니 하나통장 연결 해지
    DISCONNECTED: 'DISCONNECTED',
    // 3.1: 미래에셋 페이머니 신설 이후 네통 개설 고객 (정상 결제 가능)
    READY_OPEN: 'READY_OPEN',
    // 3.2: 미래에셋 페이머니 신설 이전 네통 개설 고객 (추후 삭제될 버전 정보) (정상 결제 가능)
    NOT_READY_OPEN: 'NOT_READY_OPEN',
    // 4.0: 페이머니 - 하나통장 개설 (정상)
    OPEN: 'OPEN',
    // 4.1: 거래중지
    STOP_TRADING: 'STOP_TRADING',
    // 4.2: 하나통장 간편결제 계좌 삭제
    EASY_BANK_DELETED: 'EASY_BANK_DELETED',
}

/** 페이머니 가입자 상태들 */
export const PayMoneyUserStatusList: PayMoneyUserStatusType[] = [
    PayMoneyUserStatus.OPEN,
    PayMoneyUserStatus.STOP_TRADING,
    PayMoneyUserStatus.EASY_BANK_DELETED,
]

/** 페이머니 결제 가능한 상태들 */
export const PayMoneyUserAvailablePayStates: PayMoneyUserStatusType[] = [
    PayMoneyUserStatus.NOT_OPEN,
    PayMoneyUserStatus.DISCONNECTED,
    PayMoneyUserStatus.OPEN,
    PayMoneyUserStatus.READY_OPEN,
    PayMoneyUserStatus.NOT_READY_OPEN,
]

/**
 * 충전포인트 코인 타입
 * @deprecated - payMoney.bankCode로 판단
 */
export const ChargePointCoinType = {
    /** 페이머니 미가입자 */
    DEFAULT: 'DEFAULT',
    /** 머니통장 하나은행 */
    HANA: 'HANA',
    /** 머니통장 미래에셋 */
    MIRAEASSET: 'MIRAEASSET',
    /** 우리은행 */
    WOORI: 'WOORI',
} as const

/** @deprecated */
export type ChargePointCoinTypeType = Constant<typeof ChargePointCoinType>

/** 은행 결제 서비스 점검 상태 */
export const BankEasyPayMaintenanceStauts = {
    /** 개별 점검 */
    BANK_INSPECT: 'BANK_INSPECT',
    /** 공동망 점검 */
    COMMON_INSPECT: 'COMMON_INSPECT',
    /** 전체 점검 */
    ALL_BANK_INSPECT: 'ALL_BANK_INSPECT',
    /** 은행 결제 데이터 전달하는 중간 서비스에서 거는 전체 점검(ex 주문형 BE, 결제형 BE) */
    SERVICE_MAINTENANCE: 'SERVICE_MAINTENANCE',
} as const

export type BankEasyPayMaintenanceStautsType = Constant<typeof BankEasyPayMaintenanceStauts>

/** 주문서에 따른 페이머니 UI 로직 관리를 위한 상수 */
export const ChargeLimitScene = {
    /** 기본 상태 */
    DEFAULT: 'DEFAULT',
    /** 점검중 UI 표시 */
    UNDER_MAINTENANCE: 'UNDER_MAINTENANCE',
} as const

export type ChargeLimitSceneType = Constant<typeof ChargeLimitScene>

export const CreditCardBillDiscountEvent = {
    /** 청구할인 행사 */
    CS: 'CS',
    /** 포인트적립 행사 */
    SV: 'SV',
    MCS: 'MCS',
    SSV: 'SSV',
} as const

export type CreditCardBillDiscountEventType = Constant<typeof CreditCardBillDiscountEvent>

/**  모바일 간편결제 비밀번호 상태 */
export const MobileEasyPayPinStatus = {
    IDLE: 'IDLE',
    /** 비밀번호 변경 */
    CHANGE: 'CHANGE',
    /** 비밀번호 분실 */
    FORGOT: 'FORGOT',
    /** 간편결제 해지 */
    CLEAN: 'CLEAN',
} as const

export type MobileEasyPayPinStatusType = Constant<typeof MobileEasyPayPinStatus>

export const TextSimplePay = {
    /** 계좌 */
    ACCOUNT: '계좌',
    /** 간편결제 카드 */
    EASY_PAY_CARD: '간편결제 카드',
    /** 간편결제 계좌 */
    EASY_PAY_ACCOUNT: '간편결제 계좌',
}

export const MINIMUM_MONTHLY_PAY_PRICE = 50000
export const HYUNDAI_CARD_MINIMUM_MONTHLY_PAY_PRICE = 10000

export const PreferredPoint = {
    REWARD_POINT: 'REWARD_POINT',
    CHARGE_POINT: 'CHARGE_POINT',
} as const

export type PreferredPointType = Constant<typeof PreferredPoint>

export const PayPartnershipItemCode = {
    MIRAEASSET_CMA: 'MIRAEASSET_CMA',
    PAYMONEY: 'PAYMONEY',
} as const

export const PromotionStatus = {
    /** 조회 가능한 프로모션이 없는경우  */
    NO_PROMOTION: 'NO_PROMOTION',
    /** 조회시 사용가능한 프로모션이 없는경우 */
    NO_AVAILABLE_PROMOTION: 'NO_AVAILABLE_PROMOTION',
    /** 사용 가능 */
    AVAILABLE: 'AVAILABLE',
    /** (프로모션이 존재하지만) 사용 불가능  */
    UNAVAILABLE: 'UNAVAILABLE',
    /** @TODO 초기화 필요한 상태 -> 네이밍 명시적으로 리팩토링 */
    SHOULD_INIT: 'SHOULD_INIT',
    /** 재조회 필요한 상태 */
    SHOULD_RELOAD: 'SHOULD_RELOAD',
    /** 간편계좌 추가 필요한 상태 체크 */
    CHECK_CONNECTING_EASY_BANK: 'CHECK_CONNECTING_EASY_BANK',
    /** 나의할인가 프로모션 정책 있는 경우 */
    HAS_MY_OPTIMIZED_PROMOTION: 'HAS_MY_OPTIMIZED_PROMOTION',
} as const

export type PromotionStatusType = Constant<typeof PromotionStatus>

export const PromotionCardCode = {
    SAMSUNG: PaymentsCardCode.SAMSUNG,
    SHINHAN: PaymentsCardCode.SHINHAN,
    HYUNDAI: PaymentsCardCode.HYUNDAI,
    KB: PaymentsCardCode.KB,
    LOTTE: PaymentsCardCode.LOTTE,
    HANA: PaymentsCardCode.HANA,
    NH: PaymentsCardCode.NH,
    WOORI: PaymentsCardCode.WOORI,
    BC: PaymentsCardCode.BC,
} as const

export type PromotionCardCodeType = Constant<typeof PromotionCardCode>

export const PromotionCardProductNameCode = {
    /** 라인프렌즈 신한카드 */
    LINE_FRIENDS_SHINHAN: 'CPG_LG_LF',
    /** 네이버 현대카드 */
    NAVER_HYUNDAI: 'CPG_DI_NV',
    /** 네이버페이 taptap */
    NAVER_TAPPTAP: 'CPG_SS_NP',
    /** 네이버웹툰 삼성iD카드 */
    NAVER_WEBTOON_SAMSUNG: 'CPG_SS_NW',
    /** 네이버페이 머니카드 */
    NAVER_PAY_MONEY: 'CPG_BC_NP',
    /** 쇼핑엔로카 */
    SHOPPING_N_LOKA_LOTTE: 'CPG_LO_SN',
    /** 국민행복 삼성카드 */
    KOOKMIN_HAPPINESS_SAMSUNG: 'CPG_SS_KM',
    /** IBK 나라사랑카드 */
    IBK_NARA_SARANG: 'CPG_BC_IBK',
    /** 국민행복 신한카드 */
    KOOKMIN_HAPPINESS_SHINHAN: 'CPG_LG_KM',
} as const

export type PromotionCardProductNameCodeType = Constant<typeof PromotionCardProductNameCode>

export const PromotionType = {
    /** 일반 프로모션 */
    GENERAL: 'GENERAL',
    /** 카드상품 프로모션 */
    CARD_PRODUCT: 'CARD_PRODUCT',
} as const

export type CardPromotionType = Constant<typeof PromotionType>

export const PromotionUnApplicableReason = {
    TOO_MANY_PROMOTION: 'TOO_MANY_PROMOTION',
    MINIMUM_PRODUCT_PRICE: 'MINIMUM_PRODUCT_PRICE',
} as const

export type PromotionUnApplicableReasonType = Constant<typeof PromotionUnApplicableReason>

/** 프로모션 validation skip  */
export const PromotionValidationSkipType = {
    /** 프로모션 제안 검증 */
    SUGGEST_PROMOTION: 'SUGGEST_PROMOTION',
    /** 프로모션 간편계좌 연결 검증 */
    CONNECT_BANK: 'CONNECT_BANK',
} as const

export type PromotionValidationSkipTypeType = Constant<typeof PromotionValidationSkipType>

/** 프로모션 중복 참여 제한 타입  */
export const PromotionRestriction = {
    /** 제한 없음 */
    NONE: 'NONE',
    /** 1일 내 */
    DAY: 'DAY',
    /** 기간 내 */
    PERIOD: 'PERIOD',
} as const

export type PromotionRestrictionType = Constant<typeof PromotionRestriction>

/** 네이버 포인트 사용 가능 여부 상태  */
export const NaverPointStatus = {
    /** 사용 불가능(가능 상태로 변경될 수 없음) */
    NEVER: 'NEVER',
    /** 사용 가능 */
    ENABLE: 'ENABLE',
    /** 사용 불가능(가능 상태로 변경될 수 있음) */
    DISABLE: 'DISABLE',
} as const

export type NaverPointStatusType = Constant<typeof NaverPointStatus>

/** 제휴포인트 전환 가능 여부 상태  */
export const AffiliatePointStatus = {
    /** 전환 불가능 */
    NEVER: 'NEVER',
    /** 전환 가능 */
    ENABLE: 'ENABLE',
} as const

export type AffiliatePointStatusType = Constant<typeof AffiliatePointStatus>

/** 간편카드 한정 BillingCardCode */
export const EasyCardBillingCode = {
    /** 신한 */
    SHINHAN: PaymentsCardCode.SHINHAN,
    /** 비씨 */
    BC: PaymentsCardCode.BC,
    /** 국민 */
    KB: PaymentsCardCode.KB,
    /** 농협 */
    NH: PaymentsCardCode.NH,
    /** 롯데 */
    LOTTE: PaymentsCardCode.LOTTE,
    /** 삼성 */
    SAMSUNG: PaymentsCardCode.SAMSUNG,
    /** 씨티 */
    CITY: PaymentsCardCode.CITY,
    /** 우리 */
    WOORI: PaymentsCardCode.WOORI,
    /** 카카오뱅크 */
    KAKAO: PaymentsCardCode.KAKAO,
    /** 현대 */
    HYUNDAI: PaymentsCardCode.HYUNDAI,
    /** 하나 */
    HANA: PaymentsCardCode.HANA,
} as const

export type EasyCardBillingCodeType = Constant<typeof EasyCardBillingCode>

export const CardCompanyId = {
    /** 비씨카드 */
    BC: 'CCBC',
    /** 제주카드 */
    JEJU: 'CCCJ',
    /** 국민카드 */
    KB: 'CCKM',
    /** 수협카드 */
    SUHYUP: 'CCSU',
    /** 광주카드 */
    KWANGJU: 'CCKJ',
    /** 전북카드 */
    JEONBUK: 'CCJB',
    /** 우리카드 */
    WOORIE: 'CCWR',
    /** 신한카드 */
    SHINHAN: 'CCLG',
    /** 삼성카드 */
    SAMSUNG: 'CCSS',
    /** 현대카드 */
    HYUNDAI: 'CCDI',
    /** 롯데카드 */
    LOTTE: 'CCLO',
    /** 외환카드 */
    KEB: 'CCKE',
    /** 하나카드 */
    HANA: 'CCHN',
    /** 농협카드 */
    NH: 'CCNH',
    /** 씨티카드 */
    CITY: 'CCCT',
    /** 카카오뱅크카드 */
    KAKAOBANK: 'CCKA',
    /** 기타 */
    ETC: 'CCNC',
} as const
export type CardCompanyIdType = Constant<typeof CardCompanyId>

/**
 * DCB 카드코드 > 간편결제 카드코드로 매핑
 */
export const mapCompanyIdToPaymentCompanyCode: Record<CardCompanyIdType, PaymentsCardCodeType> = {
    /** BC카드  */
    [CardCompanyId.BC]: PaymentsCardCode.BC,
    /** 제주카드 */
    [CardCompanyId.JEJU]: PaymentsCardCode.JEJU,
    /** 국민카드 */
    [CardCompanyId.KB]: PaymentsCardCode.KB,
    /** 수협카드 */
    [CardCompanyId.SUHYUP]: PaymentsCardCode.SUHYUP,
    /** 광주카드 */
    [CardCompanyId.KWANGJU]: PaymentsCardCode.GWANGJU,
    /** 전북카드 */
    [CardCompanyId.JEONBUK]: PaymentsCardCode.JB,
    /** 우리카드 */
    [CardCompanyId.WOORIE]: PaymentsCardCode.WOORI,
    /** 신한카드 */
    [CardCompanyId.SHINHAN]: PaymentsCardCode.SHINHAN,
    /** 삼성카드 */
    [CardCompanyId.SAMSUNG]: PaymentsCardCode.SAMSUNG,
    /** 현대카드 */
    [CardCompanyId.HYUNDAI]: PaymentsCardCode.HYUNDAI,
    /** 롯데카드 */
    [CardCompanyId.LOTTE]: PaymentsCardCode.LOTTE,
    /** 외환카드 */
    [CardCompanyId.KEB]: PaymentsCardCode.KEB,
    /** 하나카드 */
    [CardCompanyId.HANA]: PaymentsCardCode.HANA,
    /** 농협카드 */
    [CardCompanyId.NH]: PaymentsCardCode.NH,
    /** 씨티카드 */
    [CardCompanyId.CITY]: PaymentsCardCode.CITY,
    /** 카카오뱅크카드 */
    [CardCompanyId.KAKAOBANK]: PaymentsCardCode.KAKAO,
    /** 기타 */
    [CardCompanyId.ETC]: PaymentsCardCode.ETC,
}

export const mapCardOwnerToGroupType: Record<CardOwnerType, CardCodeType> = {
    [CardOwner.PERSONAL]: CardOwnerGroup.PRIVATE,
    [CardOwner.FAMILY]: CardOwnerGroup.PRIVATE,
    [CardOwner.PREPAID]: CardOwnerGroup.PRIVATE,
    [CardOwner.CORP]: CardOwnerGroup.CORPORATION,
    [CardOwner.CORP_PUBLIC]: CardOwnerGroup.CORPORATION,
}

export const PaymentMethodModalCase = {
    /** 네이버페이 머니 결제 TooltipModal */
    PAY_MONEY_TOOLTIP: 'PAY_MONEY_TOOLTIP',
    /** 네이버페이 충전결제 TooltipModial */
    RECHARGE_TOOLTIP: 'RECHARGE_TOOLTIP',
    /** 무통장입금 TooltipModal */
    PAY_NEXT_TOOLTIP: 'PAY_NEXT_TOOLTIP',
    /** 무통장입금 본인 계좌 환불 안내 TooltipModal */
    PAY_NEXT_REFUND_TOOLTIP: 'PAY_NEXT_REFUND_TOOLTIP',
    /** 도착보장 상품 무통장입금 불가 TooltipModal */
    PAY_NEXT_DISABLED_TOOLTIP: 'PAY_NEXT_DISABLED_TOOLTIP',
    /** 카드사 결제할인(카드 프로모션) TooltipModal */
    CARD_PROMOTION_TOOLTIP: 'CARD_PROMOTION_TOOLTIP',
    /** 혜택카드 적립 TooltipModal */
    BENEFIT_CARD_TOOLTIP: 'BENEFIT_CARD_TOOLTIP',
    /** 카드 간편결제 할부 Bottom Modal */
    INSTALLMENT_SELLECT: 'INSTALLMENT_SELLECT',
    /** 신용카드 선택 Modal */
    CREDIT_CARD_SELECT: 'CREDIT_CARD_SELECT',
    /** 신용카드 할부 선택 Modal */
    CREDIT_CARD_INSTALLMENT_SELECT: 'CREDIT_CARD_INSTALLMENT_SELECT',
    /** 나중에결제 은행 선택 Modal */
    PAY_NEXT_BANK_SELECT: 'PAY_NEXT_BANK_SELECT',
    /** 문화누리카드 카드 선택 Modal */
    WELFARE_CARD_SELECT: 'WELFARE_CARD_SELECT',
    /** 4.0 페이머니 출금 가능 금액 안내 Modal */
    AVAILABLE_WITHDRAW_PAYMONEY: 'AVAILABLE_WITHDRAW_PAYMONEY',
    /** 폐밀리결제 TooltipModal */
    FAMILY_PAY_TOOLTIP: 'FAMILY_PAY_TOOLTIP',
    /** 후불결제 가입 */
    PAY_LATER_SIGN_UP: 'PAY_LATER_SIGN_UP',
    /** 카드 할부정보 선택 */
    SELECT_MONTHLY_INSTALLMENT_OPTION: 'SELECT_MONTHLY_INSTALLMENT_OPTION',
    /** 결제수단 전체보기 */
    PAYMENT_METHOD_FULL_VIEW: 'PAYMENT_METHOD_FULL_VIEW',
    /** 카드 혜택 보기 */
    CARD_BENEFIT: 'CARD_BENEFIT',
    /** 무이자 혜택 보기 */
    INTEREST_FEE_BENEFIT: 'INTEREST_FEE_BENEFIT',
    /** 패밀리결제 사용불가 안내 */
    FAMILY_PAY_DISABLED: 'FAMILY_PAY_DISABLED',
    /** 패밀리결제 가입유도 */
    FAMILY_PAY_ENTRANCE_GUIDE: 'FAMILY_ENTRANCE_GUIDE',
}

export type PaymentMethodModalCaseType = Constant<typeof PaymentMethodModalCase>

/** 결제수단 타입 ENUM */
export const PAYMENT_PRIORITY = {
    /** 1차 결제수단 */
    PRIMARY: 'PRIMARY',
    /** 2차 결제수단 */
    SECONDARY: 'SECONDARY',
} as const

export type PaymentPriorityType = Constant<typeof PAYMENT_PRIORITY>

/** 노출주문상태 */
export const ExposureStatus = {
    결제진행중: '결제진행중',
    결제대기중: '결제대기중',
    결제완료: '결제완료',
    상품준비중: '상품준비중',
    발송준비중: '발송준비중',
    발송지연: '발송지연',
    배송준비중: '배송준비중',
    배송중: '배송중',
    배송완료: '배송완료',
    발송완료: '발송완료',
    구매확정연장: '구매확정연장',
    구매확정완료: '구매확정완료',
    신규주문: '신규주문',
    미결제취소완료: '미결제취소완료',
    취소요청: '취소요청',
    취소중: '취소중',
    취소완료: '취소완료',
    결제취소: '결제취소',
    부분취소: '부분취소',
    반품요청: '반품요청',
    반품중: '반품중',
    반품수거완료: '반품수거완료',
    반품환불완료: '반품환불완료',
    교환요청: '교환요청',
    교환중: '교환중',
    교환수거완료: '교환수거완료',
    교환재배송중: '교환재배송중',
    교환완료: '교환완료',
    보류접수: '보류접수',
    재배송중: '재배송중',
    구매확정요청: '구매확정요청',
    직권취소중: '직권취소중',
    직권취소완료: '직권취소완료',
    취소철회: '취소철회',
    반품철회: '반품철회',
    교환철회: '교환철회',
    구매완료: '구매완료',
    구매취소: '구매취소',
    자동구매확정제외: '자동구매확정제외',
    예매완료: '예매완료',
    예매취소마감: '예매취소마감',
    예매취소: '예매취소',
    결제처리중오류: '결제처리중오류',
    정산완료: '정산완료',
} as const

export type ExposureStatusType = Constant<typeof ExposureStatus>

/**
 * ecoupon 상태 (별도의 타입 없이 문구 그대로 관리)
 * @see https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2865#issuecomment-2318878
 */
export const EcouponStatus = {
    사용가능: '사용가능',
    기간만료: '기간만료',
    사용불가: '사용불가',
    사용완료: '사용완료',
} as const
export type EcouponStatusType = Constant<typeof EcouponStatus>

/** 상세 내 표시될 주결제수단 */
export const DetailPaymentClass = {
    CREDIT_CARD: 'CREDIT_CARD',
    BANK: 'BANK',
    VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
    MOBILE: 'MOBILE',
    MOBILE_EASY_PAY: 'MOBILE_EASY_PAY',
    PAY_LATER: 'PAY_LATER',
    DON: 'DON',
    CHARGE_AMOUNT: 'CHARGE_AMOUNT',
    NAVER_CASH: 'NAVER_CASH',
    CHECKOUT_ACCUMULATION: 'CHECKOUT_ACCUMULATION',
    CHARGED_POINT: 'CHARGED_POINT',
    SELLER_REMIT: 'SELLER_REMIT',
    POINT: 'POINT',
    CULTURE_GIFT_CERTIFICATE: 'CULTURE_GIFT_CERTIFICATE',
    BOOKS_GIFT_CERTIFICATE: 'BOOKS_GIFT_CERTIFICATE',
    AUTOMATIC_ANSWERING_SERVICE: 'AUTOMATIC_ANSWERING_SERVICE',
    TELEPHONE_RATING: 'TELEPHONE_RATING',
    BOOKING_CERTIFICATE: 'BOOKING_CERTIFICATE',
    CREDIT_CARD_EASY_PAY: 'CREDIT_CARD_EASY_PAY',
    BANK_EASY_PAY: 'BANK_EASY_PAY',
    LINEPAY_BALANCE: 'LINEPAY_BALANCE',
    SKIP: 'SKIP',
    FAMILY_PAY: 'FAMILY_PAY',
    SAMSUNG_PAY: 'SAMSUNG_PAY',
    APPLE_PAY: 'APPLE_PAY',
    NONE: 'NONE',
} as const

export type DetailPaymentClassType = Constant<typeof DetailPaymentClass>

export const OfflinePaymentStatus = {
    QR_NOT_SUPPORTED: 'QR_NOT_SUPPORTED',
    SWIPE_NOT_SUPPORTED: 'SWIPE_NOT_SUPPORTED',
    MPM_NOT_SUPPORTED: 'MPM_NOT_SUPPORTED',
    O2O_NOT_SUPPORTED: 'O2O_NOT_SUPPORTED',
    QR_BLOCKED: 'QR_BLOCKED',
} as const

export type OfflinePaymentStatusType = Constant<typeof OfflinePaymentStatus>

export const CardAuthType = {
    OTC_V1: 'OTC_V1',
    OTC_V2: 'OTC_V2',
} as const

export type CardAuthTypeType = Constant<typeof CardAuthType>

/** 카드 플레이트 아이템 타입 */
export const PlateItemType = {
    /** 결제수단 전체보기 */
    FULL_VIEW: 'FULL_VIEW',
    /** 간편카드 추가 플레이트 */
    ADD_CARD: 'ADD_CARD',
    /** 조회 실패 플레이트 */
    LOAD_FAIL: 'LOAD_FAIL',
    /** 간편카드 아이템 */
    CARD: 'CARD',
    /** 간편계좌 아이템 */
    BANK: 'BANK',
} as const
export type PlateItemTypeType = Constant<typeof PlateItemType>

// 네이버 현대카드 ED1
export const HYUNDAI_CARD_PRODUCT_CODE = '02918'

// 네이버 현대카드 ED2
export const HYUNDAI_CARD_PRODUCT_CODE_VER2 = '03297'

// 라인프렌즈 신한카드 상품코드
export const LINE_FRIENDS_SHINHAN = 'BOAC76'
